import React, { ReactNode } from 'react';
import moment from 'moment';
import { DateFormats, NO_DATA } from 'constants/common';
import { DeleteOutline, Edit, Done } from '@mui/icons-material';
import { Box, Stack, Typography, Divider, Button } from '@mui/material';
import { DealActivityParticipationType } from '@trnsact/trnsact-shared-types';

import { EventNotes } from './EventNotes';
import { calendarApi, useCalendar } from '../../api';
import { CalendarEventCardProps } from '../../types';
import { useDealActivityMutations } from '../../../api';
import { DueCell, StatusCell, AccountCell, EventTypeCell, PriorityCell } from '../table/cells';

interface Props extends CalendarEventCardProps {
  handleClose: VoidFunction;
}

export const CalendarEventInfo = ({ event, onSwitchMode, handleClose }: Props) => {
  const { handleCompleteEvent, isCompleting } = useCalendar();
  const { handleDeleteDealActivity, isDealActivityDeleting } = useDealActivityMutations();

  const formatDateTime = (value?: string | null, allDay?: boolean) => {
    if (!value) return NO_DATA;

    return moment(value).format(allDay ? DateFormats.Medium : DateFormats.DateTimeMedium12Hour);
  };

  const start = event.allDay ? event.startDate : event.startAt;
  const end = event.allDay ? event.endDate : event.endAt;

  const fields: { label: string; content: ReactNode; fullWidth?: boolean }[] = [
    {
      label: 'Account',
      content: (
        <AccountCell
          account={event.participants.reduce((acc, participant) => {
            if (participant.participationType === DealActivityParticipationType.Internal) return acc;
            return acc + participant.account?.name || NO_DATA;
          }, '')}
        />
      ),
    },
    {
      label: 'Type',
      content: <EventTypeCell type={event.eventType} />,
    },
    {
      label: 'Due',
      content: <DueCell due={event.due ?? null} allDay={event.allDay} />,
    },
    {
      label: 'Contact',
      content: (
        <Typography variant="body2">
          {event.participants.reduce((acc, participant) => {
            if (participant.participationType === DealActivityParticipationType.Internal) return acc;
            return acc + participant.fullName || NO_DATA;
          }, '')}
        </Typography>
      ),
    },
    {
      label: 'Status',
      content: <StatusCell status={event.activityStatus} />,
    },
    {
      label: 'Priority',
      content: <PriorityCell priority={event.priority} />,
    },
    {
      label: 'Starts',
      content: <Typography variant="body2">{formatDateTime(start, event.allDay)}</Typography>,
    },
    {
      label: 'Ends',
      content: <Typography variant="body2">{formatDateTime(end, event.allDay)}</Typography>,
    },
    {
      label: 'All Day',
      content: <Typography variant="body2">{event.allDay ? 'Yes' : 'No'}</Typography>,
    },
    {
      label: 'Created At',
      content: (
        <Typography variant="body2">
          {event.createdDateTime ? moment(event.createdDateTime).format(DateFormats.DateTimeShort24Hour) : NO_DATA}
        </Typography>
      ),
    },
    {
      label: 'Completed At',
      content: (
        <Typography variant="body2">
          {event.isCompleted ? moment(event.completedAt).format(DateFormats.DateTimeShort24Hour) : NO_DATA}
        </Typography>
      ),
    },
    {
      label: 'Description',
      fullWidth: true,
      content: <Typography variant="body2">{event.description || NO_DATA}</Typography>,
    },
  ];

  return (
    <>
      <Stack height="100%" direction="column" gap="0.5rem">
        <Stack direction="column">
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography color="text.secondary">Activity</Typography>

            {event.updatedDateTime && (
              <Typography color="text.secondary">
                Last update: {moment(event.updatedDateTime).format(DateFormats.DateTimeShort24Hour)}
              </Typography>
            )}
          </Stack>

          <Typography variant="h6" fontWeight="bold" textTransform="none">
            Event {event.subject}
          </Typography>
        </Stack>

        <Divider sx={{ borderColor: 'divider' }} />

        <Box sx={{ display: 'grid', gap: '1rem', gridTemplateColumns: '1fr 1fr' }}>
          {fields.map(({ label, content, fullWidth }) => (
            <Stack key={label} gap="0.25rem" sx={{ gridColumn: fullWidth ? '1 / -1' : 'auto' }}>
              <Typography variant="caption" fontWeight={600} color="text.secondary" textTransform="uppercase">
                {label}
              </Typography>

              {content}
            </Stack>
          ))}
        </Box>

        <Divider sx={{ borderColor: 'divider' }} />

        <EventNotes id={event.id} />
      </Stack>

      <Stack direction="row" gap="1rem" width="100%">
        <Button
          fullWidth
          variant="contained"
          startIcon={<Done />}
          loading={isCompleting}
          disabled={isDealActivityDeleting}
          onClick={async () => {
            await handleCompleteEvent([event.id], api => ({
              awaitRefetchQueries: true,
              refetchQueries: [api.calendarEventsListing],
            }));

            handleClose();
          }}
        >
          Complete
        </Button>

        <Button fullWidth onClick={onSwitchMode} startIcon={<Edit color="primary" />} disabled={isDealActivityDeleting}>
          Update
        </Button>

        <Button
          fullWidth
          color="error"
          startIcon={<DeleteOutline color="error" />}
          disabled={isDealActivityDeleting}
          onClick={async () => {
            await handleDeleteDealActivity({ id: event.id }, () => ({
              awaitRefetchQueries: true,
              refetchQueries: [calendarApi.calendarEventsListing],
            }));

            handleClose();
          }}
        >
          Delete
        </Button>
      </Stack>
    </>
  );
};
